import type { IconName } from '../components/Icon';
import type { Permission } from '../api/types';
import { isReleased } from '../config/release';

/**
 * The dashboard's information architecture (spec §19).
 *
 * Navigation is driven by <em>permissions</em>, never by role names. That matters for more than
 * tidiness: the permission set comes from the same enum the backend authorises against, so a menu
 * entry and the endpoint behind it cannot disagree about who may use it. Adding a role, or moving
 * a permission between roles, changes both sides at once.
 *
 * Every destination is declared now, including the ones whose screens arrive later, because the
 * shape of the product is a decision already made (spec §19) and because it lets the role-based
 * navigation be tested for real at Milestone 0. Destinations whose milestone has not landed render
 * a placeholder rather than a broken link.
 */

export interface NavigationItem {
  /** Stable key, also the i18n key under `nav.items`. */
  readonly key: string;
  /**
   * The mark beside the label in the sidebar.
   *
   * Declared here rather than looked up in the shell because it is part of what
   * a destination *is*: the sidebar, the empty states and any future command
   * palette all want the same drawing for the same place, and a second map in a
   * component is how the day board ends up with one icon in the nav and a
   * different one on its own heading.
   */
  readonly icon: IconName;
  readonly path: string;
  /** Visible when the viewer holds *any* of these; empty means "any authenticated user". */
  readonly requires: readonly Permission[];
  /** Milestone that makes this destination real. */
  readonly milestone: string;
}

export interface NavigationGroup {
  /** Stable key, also the i18n key under `nav.groups`. */
  readonly key: string;
  readonly items: readonly NavigationItem[];
}

export const NAVIGATION: readonly NavigationGroup[] = [
  {
    key: 'operate',
    items: [
      { key: 'dayBoard', icon: 'board', path: '/day-board', requires: ['APPOINTMENT_READ'], milestone: 'M3' },
      // CR-29: every appointment over a range and across branches; the day board is one day at one.
      { key: 'appointments', icon: 'calendar', path: '/appointments', requires: ['APPOINTMENT_READ'], milestone: 'M3' },
      { key: 'patients', icon: 'patients', path: '/patients', requires: ['PATIENT_READ'], milestone: 'M3' },
      // FR-306: the queue an absence or a closure leaves behind. Reading it takes WORKLIST_READ,
      // which a doctor holds too — they can see what their own absence caused, and cannot resolve it.
      { key: 'worklists', icon: 'worklists', path: '/worklists', requires: ['WORKLIST_READ'], milestone: 'M6' },
      { key: 'checkout', icon: 'checkout', path: '/checkout', requires: ['SALE_RECORD'], milestone: 'M4' },
      { key: 'chat', icon: 'chat', path: '/chat', requires: ['CHAT_PARTICIPATE'], milestone: 'M9' },
    ],
  },
  {
    key: 'configure',
    items: [
      { key: 'catalog', icon: 'catalog', path: '/catalog', requires: ['CATALOG_READ'], milestone: 'M2' },
      { key: 'packages', icon: 'packages', path: '/packages', requires: ['PACKAGE_MANAGE', 'PROMOTION_MANAGE'], milestone: 'M2' },
      { key: 'policies', icon: 'policies', path: '/policies', requires: ['POLICY_MANAGE'], milestone: 'M2' },
      { key: 'staff', icon: 'staff', path: '/staff', requires: ['STAFF_READ'], milestone: 'M1' },
      // FR-702/D-2: the desk enters the rota on a doctor's behalf, which is how these clinics work.
      // AVAILABILITY_MANAGE_ANY rather than _OWN: a doctor sets their own hours in the doctor app.
      {
        key: 'doctorHours',
        icon: 'clock',
        path: '/doctor-hours',
        requires: ['AVAILABILITY_MANAGE_ANY'],
        milestone: 'M3',
      },
      { key: 'branches', icon: 'branches', path: '/branches', requires: ['BRANCH_READ'], milestone: 'M1' },
      // ASM-006: the logo, names and accent colour the patient app renders. A clinic-administrator
      // job, gated on the same permission as the rest of the clinic's own settings.
      { key: 'branding', icon: 'branding', path: '/branding', requires: ['CLINIC_SETTINGS_MANAGE'], milestone: 'M5' },
      {
        key: 'notifications',
        icon: 'messages',
        path: '/notifications',
        requires: ['NOTIFICATION_TEMPLATE_MANAGE'],
        milestone: 'M5',
      },
      // FR-504/DEC-024: the clinic's own consent texts. CONSENT_MANAGE rather than
      // CLINIC_SETTINGS_MANAGE, because a consent form is a legal document and the person who may
      // edit the opening hours is not automatically the person who may reword it.
      { key: 'consents', icon: 'consents', path: '/consents', requires: ['CONSENT_MANAGE'], milestone: 'M7' },
      // DEC-025: what the patient is told after a treatment. Shares the template permission with
      // the notification wording, because it is the same job done by the same person.
      { key: 'aftercare', icon: 'aftercare', path: '/aftercare', requires: ['NOTIFICATION_TEMPLATE_MANAGE'], milestone: 'M7' },
      { key: 'machines', icon: 'machines', path: '/machines', requires: ['MACHINE_READ'], milestone: 'M8' },
      { key: 'inventory', icon: 'inventory', path: '/inventory', requires: ['INVENTORY_READ'], milestone: 'M8' },
      // SEC-003 puts this on the clinic's side of the fence, not the platform's: it is the clinic
      // administrator who grants Laseera time-boxed access to their data, sees it in their own
      // audit log, and can revoke it. A platform operator cannot grant themselves anything.
      { key: 'supportGrants', icon: 'key', path: '/support-access', requires: ['SUPPORT_GRANT_MANAGE'], milestone: 'M1' },
    ],
  },
  {
    key: 'money',
    items: [
      { key: 'sales', icon: 'sales', path: '/sales', requires: ['SALE_READ'], milestone: 'M4' },
      { key: 'expenses', icon: 'expenses', path: '/expenses', requires: ['EXPENSE_READ', 'EXPENSE_RECORD'], milestone: 'M4' },
      { key: 'refunds', icon: 'refunds', path: '/refunds', requires: ['REFUND_APPROVE', 'REFUND_REQUEST'], milestone: 'M4' },
      { key: 'statements', icon: 'statements', path: '/statements', requires: ['DOCTOR_STATEMENT_READ'], milestone: 'M9' },
    ],
  },
  {
    key: 'insight',
    items: [
      {
        key: 'reports',
        icon: 'reports',
        path: '/reports',
        requires: ['REPORT_OPERATIONAL_READ', 'REPORT_FINANCIAL_READ', 'REPORT_OWN_READ'],
        milestone: 'M9',
      },
      { key: 'broadcasts', icon: 'broadcasts', path: '/broadcasts', requires: ['BROADCAST_SEND'], milestone: 'M9' },
      { key: 'audit', icon: 'audit', path: '/audit', requires: ['AUDIT_LOG_READ'], milestone: 'M1' },
    ],
  },
  {
    // A separate route-space behind platform roles (§26). Clinic staff never see this group,
    // and platform operators never see the clinic groups, because the two permission sets are
    // disjoint by construction (SEC-003).
    key: 'platform',
    items: [
      { key: 'tenants', icon: 'clinics', path: '/platform/clinics', requires: ['PLATFORM_TENANT_MANAGE'], milestone: 'M1' },
      { key: 'invoices', icon: 'invoices', path: '/platform/invoices', requires: ['PLATFORM_INVOICE_MANAGE'], milestone: 'M1' },
      {
        key: 'featureFlags',
        icon: 'flags',
        path: '/platform/feature-flags',
        requires: ['PLATFORM_FEATURE_FLAG_MANAGE'],
        milestone: 'M1',
      },
      // The operator's side of SEC-003: what clinics have let them see, and the way in. Gated on
      // PLATFORM_HEALTH_READ because that is the permission both platform roles hold and no clinic
      // role does — the closest thing the enum has to "is a Laseera operator". The endpoint behind
      // it is authenticated-only by design, since holding a grant is what confers the access; this
      // is about not putting a platform console entry in a receptionist's sidebar.
      {
        key: 'platformSupport',
        icon: 'support',
        path: '/platform/support-access',
        requires: ['PLATFORM_HEALTH_READ'],
        milestone: 'M1',
      },
      // Separate from the clinic audit viewer above, and reading a different chain: what Laseera
      // did to clinics, never what happened inside one (DEC-048, SEC-003).
      { key: 'platformAudit', icon: 'audit', path: '/platform/audit', requires: ['PLATFORM_AUDIT_READ'], milestone: 'M1' },
    ],
  },
];

export function grantsAny(held: ReadonlySet<Permission>, required: readonly Permission[]): boolean {
  if (required.length === 0) {
    return true;
  }
  return required.some((permission) => held.has(permission));
}

/**
 * Filters the full map down to what this viewer may reach; empty groups disappear.
 *
 * Two filters, both required: the viewer's permissions, and whether the destination has been
 * released yet (`config/release.ts`). Tests about permissions pass `() => true` for the second, so
 * they keep checking roles against the whole map whatever stage the release has reached.
 */
export function visibleNavigation(
  held: ReadonlySet<Permission>,
  released: (key: string) => boolean = isReleased,
): NavigationGroup[] {
  return NAVIGATION.map((group) => ({
    key: group.key,
    items: group.items.filter((item) => released(item.key) && grantsAny(held, item.requires)),
  })).filter((group) => group.items.length > 0);
}

/** Every destination the viewer may reach, flattened; used for route guards. */
export function reachablePaths(
  held: ReadonlySet<Permission>,
  released: (key: string) => boolean = isReleased,
): string[] {
  return visibleNavigation(held, released).flatMap((group) => group.items.map((item) => item.path));
}

export function findItemByPath(path: string): NavigationItem | undefined {
  return NAVIGATION.flatMap((group) => group.items).find((item) => item.path === path);
}
